/**
 * Reading record history, and the recycle bin.
 *
 * History rows live in `history` in the flickquote-system database (see
 * lib/history.js for what they hold). Row security does the access control:
 * an entry carries the read grants of the record it is about, so these
 * queries return exactly what the signed-in user may see. Nothing here writes
 * history — only record-history-capture does.
 *
 * The recycle bin is NOT read from history. It lists the soft-delete tables
 * themselves (`deletedAt` set), so a change history missed, or one that has
 * expired, can never hide a deleted record. History only adds who deleted it.
 */

import { databases, DATABASE_ID, Query, getCurrentUser } from './appwrite';
import { HISTORY_DATABASE_ID, HISTORY_TABLE, mergePages } from '../lib/history';
import { SOFT_DELETE_TABLES } from '../lib/records';

/** Entries per page in the drawer and in Settings → History. */
export const PAGE_SIZE = 50;

/** Deleted rows listed per table in the recycle bin. */
const BIN_PER_TABLE = 100;

const listHistory = (queries) =>
  databases.listDocuments(HISTORY_DATABASE_ID, HISTORY_TABLE, queries).then((r) => r.documents || []);

/**
 * One page of a record's history: its own entries and its children's (a quote
 * request's photos, notes, quote and damage; a customer's vehicles), newest
 * first.
 *
 * `before` and `seen` come from the previous page. Returns
 * `{ entries, before, done }`; pass `before` back, with the ids already shown in
 * `seen`, for the next page.
 */
export async function recordHistory(rowId, { before = null, seen = new Set(), limit = PAGE_SIZE } = {}) {
  const page = (field) => {
    const queries = [Query.equal(field, rowId), Query.orderDesc('rowUpdatedAt'), Query.limit(limit)];
    if (before) queries.push(Query.lessThanEqual('rowUpdatedAt', before));
    return listHistory(queries);
  };
  const [own, children] = await Promise.all([page('rowId'), page('parentRowId')]);
  return mergePages([own, children], limit, seen);
}

/**
 * One page of the team's history for Settings → History, newest first.
 *
 *   tables   only these tables (a record-type filter), or all
 *   actor    a user id, or 'system' for changes made by functions
 *   from/to  ISO dates, inclusive
 *   action   e.g. 'delete'
 */
export async function teamHistory({ tables = null, actor = null, from = null, to = null, action = null, before = null, limit = PAGE_SIZE } = {}) {
  const user = await getCurrentUser();
  if (!user?.teamId) return { entries: [], before: null, done: true };

  const queries = [Query.equal('organisationId', user.teamId), Query.orderDesc('rowUpdatedAt'), Query.limit(limit)];
  if (tables?.length) queries.push(Query.equal('tableName', tables));
  if (actor === 'system') queries.push(Query.isNull('actorUserId'));
  else if (actor) queries.push(Query.equal('actorUserId', actor));
  if (action) queries.push(Query.equal('action', action));
  if (from) queries.push(Query.greaterThanEqual('rowUpdatedAt', from));
  if (to) queries.push(Query.lessThanEqual('rowUpdatedAt', to));
  if (before) queries.push(Query.lessThan('rowUpdatedAt', before));

  const entries = await listHistory(queries);
  return {
    entries,
    before: entries.length ? entries[entries.length - 1].rowUpdatedAt : before,
    done: entries.length < limit,
  };
}

const chunk = (list, size = 100) =>
  Array.from({ length: Math.ceil(list.length / size) }, (_, i) => list.slice(i * size, (i + 1) * size));

/** The latest `delete` entry for each of these rows, by row id. */
async function deletionsOf(rowIds) {
  const byRow = new Map();
  for (const ids of chunk(rowIds)) {
    const entries = await listHistory([
      Query.equal('rowId', ids),
      Query.equal('action', 'delete'),
      Query.orderDesc('rowUpdatedAt'),
      Query.limit(500),
    ]).catch(() => []); // History is a nicety here: the bin works without it.
    for (const entry of entries) if (!byRow.has(entry.rowId)) byRow.set(entry.rowId, entry);
  }
  return byRow;
}

/** Names for deleted quote requests: the customer and the vehicle, as the list shows them. */
async function quoteRequestNames(rows) {
  const lookup = async (table, ids) => {
    const map = new Map();
    for (const part of chunk([...new Set(ids.filter(Boolean))])) {
      const result = await databases.listDocuments(DATABASE_ID, table, [Query.equal('$id', part), Query.limit(part.length)])
        .catch(() => ({ documents: [] }));
      for (const doc of result.documents || []) map.set(doc.$id, doc);
    }
    return map;
  };
  const [customers, vehicles] = await Promise.all([
    lookup('customers', rows.map((r) => r.customerId)),
    lookup('customer_vehicles', rows.map((r) => r.customerVehicleId)),
  ]);
  return new Map(rows.map((row) => {
    const customer = customers.get(row.customerId);
    const vehicle = vehicles.get(row.customerVehicleId);
    const name = customer ? `${customer.firstName || ''} ${customer.lastName || ''}`.trim() : '';
    const rego = vehicle?.registration || '';
    return [row.$id, [name, rego].filter(Boolean).join(' · ')];
  }));
}

/**
 * Everything in the team's recycle bin, most recently deleted first.
 *
 * Returns `[{ table, row, deletedBy }]`, where `deletedBy` is the matching
 * history entry's actor (or null when history has none — expired, or deleted
 * before history was recorded).
 */
export async function recycleBin() {
  const user = await getCurrentUser();
  if (!user?.teamId) return [];

  const perTable = await Promise.all(Object.keys(SOFT_DELETE_TABLES).map((table) =>
    databases.listDocuments(DATABASE_ID, table, [
      Query.equal('organisationId', user.teamId),
      Query.isNotNull('deletedAt'),
      Query.orderDesc('deletedAt'),
      Query.limit(BIN_PER_TABLE),
    ]).then((r) => (r.documents || []).map((row) => ({ table, row })))
      .catch((err) => {
        console.error(`Recycle bin: could not list ${table}:`, err);
        return [];
      })));

  const items = perTable.flat().sort((a, b) => Date.parse(b.row.deletedAt) - Date.parse(a.row.deletedAt));
  const [deletions, names] = await Promise.all([
    deletionsOf(items.map((i) => i.row.$id)),
    quoteRequestNames(items.filter((i) => i.table === 'quote_requests').map((i) => i.row)),
  ]);

  return items.map((item) => {
    const entry = deletions.get(item.row.$id);
    return {
      ...item,
      deletedBy: entry ? (entry.actorName || (entry.actorUserId ? 'Someone' : 'System')) : null,
      detail: names.get(item.row.$id) || '',
    };
  });
}

const historyApi = { recordHistory, teamHistory, recycleBin, PAGE_SIZE };
export default historyApi;
